'use client'

// «Записать себя»: повтор за диктором (.scratch/self-recording/spec.md).
// Ученик слушает фразу, записывает себя через MediaRecorder и сравнивает на
// слух: после «Стоп» подряд звучат диктор и своя запись. Всё в браузере:
// запись живёт только в памяти этой страницы, на сервер ничего не уходит.
//
// Своя запись играет через отдельный <audio>, а не через общий из lib/speak:
// иначе неудача блоба переключила бы на голос браузера, и вместо своей записи
// ученик услышал бы синтез. iOS Safari разрешает play() только из жеста, а
// запись готова асинхронно, поэтому в каждом клике элемент «разблокируется»
// тишиной, как и общий в lib/speak.ts.
//
// Стоит рядом с кнопкой озвучки: в слове урока (word-audio-row.tsx), в
// разговорнике (phrasebook.tsx) и на странице трудных звуков (/speak).
// Оформление: круглый значок, тот же класс, что у «Медленнее» (.tk-pill-toggle.tk-pill-icon);
// подсказки в стиле Turkido (components/hint.tsx), а не системные title.

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { Hint } from '@/components/hint'
import { MAX_RECORD_MS, formatSeconds, pickRecordingMimeType, recordErrorText } from '@/lib/record-self'
import { useSlowAudio } from '@/lib/slow-audio'
import { silentWavUrl, speakTurkish, stopSpeaking } from '@/lib/speak'

type Phase = 'idle' | 'asking' | 'recording' | 'ready' | 'playing'

// Пауза между диктором и своей записью, чтобы они не слипались.
const GAP_MS = 350

function canRecord() {
  return typeof window !== 'undefined' && typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
}
// Поддержка не меняется за время жизни страницы; на сервере и при гидратации
// false, чтобы разметка совпала, затем клиент перечитывает.
const noSubscribe = () => () => {}
const useCanRecord = () => useSyncExternalStore(noSubscribe, canRecord, () => false)

// Желейные значки в стиле озвучки (audio-orb-icon.tsx): микрофон сделан 15.10.2026
// той же моделью, что картинки "Как запомнить?" (Cloudflare FLUX).
export const MIC_ICON_SRC = '/brand/mic-icon-jelly.jpg'
const PLAY_ICON_SRC = '/brand/icons/play.jpg'
export function JellyImg({ src, size = 36 }: { src: string; size?: number }) {
  return (
    <span className="tk-jelly-btn-icon" aria-hidden style={size === 36 ? undefined : { width: size, height: size }}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={src} alt="" width={size} height={size} />
    </span>
  )
}

export function StopIcon({ size = 14 }: { size?: number }) {
  return (
    <svg aria-hidden viewBox="0 0 24 24" fill="currentColor" width={size} height={size} style={{ flexShrink: 0 }}>
      <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
  )
}

export function RecordSelf({
  text,
  audioUrl,
  className = '',
  compact,
}: {
  // Что повторяем: этим текстом играет диктор при сравнении.
  text: string
  // Готовый mp3 фразы, если он известен странице; иначе lib/speak спросит сервер.
  audioUrl?: string | null
  className?: string
  // Маленький значок размером с соседний шарик озвучки (строки словаря, реплики,
  // ответы репетитора; 15.10.2026). Без него - кнопка 44 px, как в слове урока.
  compact?: number
}) {
  const supported = useCanRecord()
  const [phase, setPhase] = useState<Phase>('idle')
  const [elapsed, setElapsed] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [clipUrl, setClipUrl] = useState<string | null>(null)
  const slow = useSlowAudio()

  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<BlobPart[]>([])
  const playerRef = useRef<HTMLAudioElement | null>(null)
  const timerRef = useRef<number | null>(null)
  const startedAtRef = useRef(0)
  // Номер запуска сравнения: новый клик или смена слова обесценивает старый.
  const playSeqRef = useRef(0)
  const clipUrlRef = useRef<string | null>(null)

  function player() {
    if (!playerRef.current) playerRef.current = new Audio()
    return playerRef.current
  }

  // Вызывать синхронно в обработчике клика: после этого элемент можно
  // запускать уже после await (iOS Safari).
  function unlockPlayer() {
    const p = player()
    p.pause()
    p.src = silentWavUrl()
    p.play().catch(() => {})
  }

  function clearTimer() {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  function releaseStream() {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
  }

  const dropClip = useCallback(() => {
    if (clipUrlRef.current) URL.revokeObjectURL(clipUrlRef.current)
    clipUrlRef.current = null
    setClipUrl(null)
  }, [])

  const stopPlayback = useCallback(() => {
    playSeqRef.current++
    stopSpeaking()
    playerRef.current?.pause()
  }, [])

  // Сброс при смене слова и при уходе со страницы: запись не переживает фразу.
  useEffect(() => {
    // Ссылки на объекты ref берём внутри эффекта: в момент очистки нужны их
    // текущие значения, а не снимок на момент подписки.
    const seq = playSeqRef
    const recorder = recorderRef
    const stream = streamRef
    const playerEl = playerRef
    const clip = clipUrlRef
    return () => {
      seq.current++
      clearTimer()
      const rec = recorder.current
      if (rec && rec.state !== 'inactive') {
        rec.ondataavailable = null
        rec.onstop = null
        try {
          rec.stop()
        } catch {}
      }
      recorder.current = null
      stream.current?.getTracks().forEach((t) => t.stop())
      stream.current = null
      playerEl.current?.pause()
      if (clip.current) URL.revokeObjectURL(clip.current)
      clip.current = null
      setClipUrl(null)
      setPhase('idle')
      setError(null)
      setElapsed(0)
    }
  }, [text])

  async function playClip(url: string): Promise<void> {
    const p = player()
    p.pause()
    p.src = url
    p.currentTime = 0
    p.playbackRate = 1
    await p.play()
    await new Promise<void>((resolve) => {
      const done = () => {
        p.removeEventListener('ended', done)
        p.removeEventListener('pause', done)
        p.removeEventListener('error', done)
        resolve()
      }
      p.addEventListener('ended', done)
      p.addEventListener('pause', done)
      p.addEventListener('error', done)
    })
  }

  // Диктор, пауза, своя запись. fromGesture=false: тишина уже проиграна в клике.
  async function compare(url: string) {
    const seq = ++playSeqRef.current
    setPhase('playing')
    try {
      await speakTurkish(text, { audioUrl, slow, fromGesture: false })
      if (seq !== playSeqRef.current) return
      await new Promise((r) => setTimeout(r, GAP_MS))
      if (seq !== playSeqRef.current) return
      await playClip(url)
    } catch {
      // Не заиграло (сеть, запрет звука): просто вернём кнопки.
    } finally {
      if (seq === playSeqRef.current) setPhase('ready')
    }
  }

  async function start() {
    setError(null)
    stopPlayback()
    unlockPlayer()
    dropClip()
    setElapsed(0)
    setPhase('asking')
    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      })
    } catch (e) {
      setPhase('idle')
      setError(recordErrorText(e instanceof DOMException ? e.name : undefined))
      return
    }
    streamRef.current = stream
    const mime = pickRecordingMimeType((t) => MediaRecorder.isTypeSupported(t))
    let rec: MediaRecorder
    try {
      rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream)
    } catch {
      releaseStream()
      setPhase('idle')
      setError(recordErrorText(undefined))
      return
    }
    chunksRef.current = []
    rec.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data)
    }
    rec.onstop = () => {
      releaseStream()
      clearTimer()
      const type = rec.mimeType || mime || 'audio/webm'
      const blob = new Blob(chunksRef.current, { type })
      chunksRef.current = []
      if (blob.size === 0) {
        setPhase('idle')
        setError('Запись пустая. Попробуйте ещё раз и говорите ближе к микрофону.')
        return
      }
      const url = URL.createObjectURL(blob)
      clipUrlRef.current = url
      setClipUrl(url)
      void compare(url)
    }
    recorderRef.current = rec
    try {
      rec.start()
    } catch {
      releaseStream()
      setPhase('idle')
      setError(recordErrorText(undefined))
      return
    }
    startedAtRef.current = Date.now()
    setPhase('recording')
    timerRef.current = window.setInterval(() => {
      const ms = Date.now() - startedAtRef.current
      setElapsed(ms)
      if (ms >= MAX_RECORD_MS) stop()
    }, 200)
  }

  function stop() {
    clearTimer()
    unlockPlayer()
    const rec = recorderRef.current
    if (rec && rec.state !== 'inactive') rec.stop()
    else setPhase('idle')
  }

  function onMainClick() {
    if (phase === 'recording') stop()
    else if (phase === 'idle' || phase === 'ready') void start()
  }

  function onCompareClick() {
    if (phase === 'playing') {
      stopPlayback()
      setPhase('ready')
      return
    }
    if (!clipUrl) return
    unlockPlayer()
    void compare(clipUrl)
  }

  if (!supported) return null

  const recording = phase === 'recording'
  const asking = phase === 'asking'
  const hasClip = phase === 'ready' || phase === 'playing'
  const mainHint = recording
    ? {
        title: 'Идёт запись',
        text: `Скажите слово и нажмите "Стоп". Запись сама остановится через ${MAX_RECORD_MS / 1000} секунд.`,
      }
    : hasClip
      ? {
          title: 'Записать заново',
          text: 'Прежняя запись сотрётся, новая прозвучит вместе с диктором.',
        }
      : {
          title: 'Записать себя',
          text: 'Повторите слово за диктором и остановите запись. Диктор и ваш голос прозвучат подряд. Запись остаётся на этом устройстве и никуда не отправляется.',
        }

  return (
    <span
      className={`tk-record-self${className ? ` ${className}` : ''}`}
      style={{
        display: 'inline-flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 6,
      }}
    >
      <Hint title={mainHint.title} text={mainHint.text} autoShowKey="tk-record-hint-shown">
        {/* Только значок (владелец 15.10.2026): микрофон, во время записи красный
            пульсирующий «Стоп» с секундами для экранного диктора. */}
        <button
          type="button"
          className={compact ? 'tk-bubble-audio-btn tk-record-btn' : 'tk-pill-toggle tk-pill-icon tk-record-btn'}
          style={compact ? { width: compact, height: compact, opacity: asking ? 0.7 : undefined } : { opacity: asking ? 0.7 : undefined }}
          data-on={recording ? '' : undefined}
          data-recording={recording ? '' : undefined}
          disabled={asking || phase === 'playing'}
          aria-label={recording ? `Остановить запись, ${formatSeconds(elapsed)}` : hasClip ? 'Записать заново' : 'Записать себя'}
          onClick={onMainClick}
        >
          {recording ? <StopIcon size={compact ? Math.round(compact * 0.55) : 18} /> : <JellyImg src={MIC_ICON_SRC} size={compact ?? 36} />}
        </button>
      </Hint>
      {hasClip && (
        <Hint title="Диктор и я" text="Сначала диктор, потом ваша запись. Нажмите ещё раз, чтобы остановить.">
          <button
            type="button"
            className={compact ? 'tk-bubble-audio-btn' : 'tk-pill-toggle tk-pill-icon'}
            style={compact ? { width: compact, height: compact } : undefined}
            data-on={phase === 'playing' ? '' : undefined}
            aria-label={phase === 'playing' ? 'Остановить' : 'Послушать диктора и свою запись подряд'}
            onClick={onCompareClick}
          >
            {phase === 'playing' ? <StopIcon size={compact ? Math.round(compact * 0.55) : 18} /> : <JellyImg src={PLAY_ICON_SRC} size={compact ?? 36} />}
          </button>
        </Hint>
      )}
      {error && (
        <span
          role="status"
          style={{
            flexBasis: '100%',
            fontSize: 13,
            lineHeight: 1.35,
            color: 'oklch(0.78 0.1 25)',
          }}
        >
          {error}
        </span>
      )}
    </span>
  )
}
